import axios from "axios";
import { useUserStore } from "@/store/modules/user.js";
import router from "../router/index.js";
import { notification } from "antdv-next";
import { getCookie } from "@/utils/storage.js";
import { ACCESS_TOKEN } from "@/store/mutation-types.js";

export const HTTP_STATUS = Object.freeze({
  OK: 200,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  SERVER_ERROR: 500
});

export const API_CODE = Object.freeze({
  SUCCESS: 200,
  CONTACT_QUOTA_REQUIRED: 2002,
  NOT_FOUND: 404,
  ROUTE_REFRESH_REQUIRED: 7007,
  UNAUTHORIZED: 401,
  TOKEN_EXPIRED: 7001,
  TOKEN_INVALID: 7002,
  TOKEN_MISSING: 7003,
  TOKEN_REVOKED: 7004
});

export const TOKEN_ERROR_CODES = Object.freeze([
  API_CODE.UNAUTHORIZED,
  API_CODE.TOKEN_EXPIRED,
  API_CODE.TOKEN_INVALID,
  API_CODE.TOKEN_MISSING,
  API_CODE.TOKEN_REVOKED
]);

const EXCEL_CONTENT_TYPE = "application/vnd.ms-excel";
const DEFAULT_FAIL_FEEDBACK = true;
let authExpiredHandled = false;

export function isSuccessCode(code) {
  return code === API_CODE.SUCCESS;
}

export function isSuccessResponse(res = {}) {
  return isSuccessCode(res.code);
}

function isTokenError(data = {}) {
  return TOKEN_ERROR_CODES.includes(data.code);
}

function isExcelResponse(response) {
  return (
    response.status === HTTP_STATUS.OK &&
    response.headers["content-type"]?.includes(EXCEL_CONTENT_TYPE)
  );
}

function getRequestOptions(config = {}) {
  return {
    ...config.config,
    failFeedback: config.failFeedback ?? config.config?.failFeedback,
    hideErrorMessage: config.hideErrorMessage ?? config.config?.hideErrorMessage
  };
}

function shouldResolveBusinessError(config = {}) {
  return getRequestOptions(config).failFeedback ?? DEFAULT_FAIL_FEEDBACK;
}

function shouldShowErrorMessage(config = {}) {
  return !getRequestOptions(config).hideErrorMessage;
}

function notifyError(config, options) {
  if (shouldShowErrorMessage(config)) {
    notification.error(options);
  }
}

function handleBusinessErrorResponse(response, resolveValue) {
  if (shouldResolveBusinessError(response.config)) {
    return resolveValue;
  }
  return Promise.reject(response.data);
}

function handleAuthExpired(error) {
  if (!authExpiredHandled) {
    authExpiredHandled = true;
    notification.error({
      message: "登录已过期",
      description: "请重新登录"
    });
    useUserStore().logout();
  }
  return Promise.reject(error);
}

// let spinNum = 0

// 创建 axios 实例
const request = axios.create({
  // API 请求的默认前缀
  baseURL: import.meta.env.VITE_API_BASE_URL
  // timeout: 6000 // 请求超时时间
});

const loginUrls = ["/auth/login", "/auth/wx-login", "/auth/wecom-login"];

// 异常拦截处理器
const errorHandler = (error) => {
  if (error.response) {
    const { status, statusText, data } = error.response;
    // 从 localstorage 获取 token
    if (status === HTTP_STATUS.UNAUTHORIZED || isTokenError(data)) {
      return handleAuthExpired(error);
    }
    if (status === HTTP_STATUS.FORBIDDEN) {
      notifyError(error.config, {
        message: "无此权限",
        description: data.message || "您无此访问权限"
      });
    }
    if (status === HTTP_STATUS.SERVER_ERROR) {
      notifyError(error.config, {
        message: "Service Error",
        description: statusText
      });
    }
    if (status === HTTP_STATUS.BAD_REQUEST) {
      notifyError(error.config, {
        message: "Forbidden",
        description: data.message
      });
    }
  } else {
    notifyError(error.config, {
      message: "Forbidden",
      description: error.toString()
    });
  }
  return Promise.reject(error);
};

// request interceptor
request.interceptors.request.use((config) => {
  const token = getCookie(ACCESS_TOKEN);
  if (loginUrls.includes(config.url)) {
    authExpiredHandled = false;
  }
  // 如果 token 存在
  // 让每个请求携带自定义 token 请根据实际情况自行修改
  if (token && !loginUrls.includes(config.url)) {
    config.headers.Authorization = token;
  }
  return config;
}, errorHandler);

// response interceptor
request.interceptors.response.use((response) => {
  if (isExcelResponse(response)) {
    return response;
  }
  if (response.data.code) {
    if (response.data.code === API_CODE.CONTACT_QUOTA_REQUIRED) {
      notifyError(response.config, {
        message: `${response.data.message}`,
        description: `如需查看用户详情/联系方式，请联系管理员添加每日访问次数！`
      });
      if (router.currentRoute.value.path.includes("/candidates/")) {
        router.push({ path: "/candidates" });
      }
      return handleBusinessErrorResponse(response);
    }
    if (response.data.code === API_CODE.NOT_FOUND) {
      notifyError(response.config, {
        message: "Not Found",
        description: response.data.message
      });
      return handleBusinessErrorResponse(response);
    }
    if (response.data.code === API_CODE.ROUTE_REFRESH_REQUIRED) {
      notifyError(response.config, {
        message: "Infomation",
        description: response.data.message
      });
      router.replace({ path: window.location.pathname });
      return handleBusinessErrorResponse(response);
    }
    if (!isSuccessResponse(response.data) && !TOKEN_ERROR_CODES.includes(response.data.code)) {
      notifyError(response.config, {
        message: response.data.code,
        description: response.data.message
      });
    }
    if (isTokenError(response.data)) {
      console.warn("Token error:", response.data.code, response.data.message);
      return handleAuthExpired(response.data);
    }
    if (!isSuccessResponse(response.data)) {
      return handleBusinessErrorResponse(response, response.data);
    }
    return response.data;
  }
}, errorHandler);

export default request;
